import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { I18nLookup } from 'pricing-pages-lib/utils/languageUtils';
import HubMap from 'self-service-api/constants/HubMap';
import { productLineToHub } from 'self-service-api/constants/ProductLineToHub';
import { API_LIMITED_ACCESS, API_NO_ACCESS } from 'self-service-api/constants/FreeUserAccessValues';
import * as PurchaseTypes from 'self-service-api/constants/PurchaseTypes';
import styled from 'styled-components';
import { getProductLineFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import FeatureTableCell from './FeatureTableCell';
import TranslatedFeature from './TranslatedFeature';
import FeatureTableUserIcon from './FeatureTableUserIcon';
import { sortApiNamesByProductLevel } from 'pricing-pages-lib/utils/sortingUtils';
const FeatureName = styled.div.attrs({
  tabIndex: -1
}).withConfig({
  displayName: "FeatureTableRow__FeatureName",
  componentId: "sc-6ef5yq-0"
})(["font-weight:600;"]);
const UserIconWrapper = styled.span.withConfig({
  displayName: "FeatureTableRow__UserIconWrapper",
  componentId: "sc-6ef5yq-1"
})(["width:2%;min-width:30px;"]);

const isSeatBased = productLine => {
  return HubMap[productLineToHub[productLine]].merchPurchaseType === PurchaseTypes.SEAT_BASED;
};

const FeatureTableRow = ({
  feature,
  highlightedProducts,
  highlightedProductsSet,
  productLine,
  onlyShowCurrentLongLimit = false
}) => {
  const highestTierProduct = highlightedProducts[highlightedProducts.length - 1];

  if (!highlightedProductsSet) {
    highlightedProductsSet = new Set(highlightedProducts);
  } // Necessary for CBX003. This (temporarily) filters out features that don't
  // apply to the highest tier of the feature table.
  // TODO: If CBX003 is productized, complete https://git.hubteam.com/HubSpot/pricing-pages-unified-ui/issues/6458.


  const sortedProductApiNames = sortApiNamesByProductLevel(feature.productApiNames);
  const shouldShowFeature = sortedProductApiNames && highlightedProductsSet.has(sortedProductApiNames[0]);

  if (!shouldShowFeature) {
    return null;
  } // This temporarily filters out features that we don't have in SSAPI.
  // There is some discrepancy between what we show currently and the features returned from the API.
  // This should be investigated and addressed as a fast follow


  const lookupVal = `pricing-pages.features.${feature.shortName}.name`;

  if (!I18nLookup(lookupVal)) {
    return null;
  }

  const accessLevel = feature.freeUserAccessLevel;
  const showUserIcon = (productLine ? isSeatBased(productLine) : feature.productApiNames.some(apiName => {
    const localProductLine = getProductLineFromApiNameString(apiName);
    return isSeatBased(localProductLine);
  })) && (accessLevel === API_LIMITED_ACCESS || accessLevel === API_NO_ACCESS);
  return /*#__PURE__*/_jsxs("tr", {
    children: [/*#__PURE__*/_jsx("th", {
      scope: "row",
      children: /*#__PURE__*/_jsxs("div", {
        className: "align-center",
        children: [/*#__PURE__*/_jsx(UserIconWrapper, {
          children: showUserIcon && /*#__PURE__*/_jsx(FeatureTableUserIcon, {
            freeUserAccess: accessLevel
          })
        }), productLine ? /*#__PURE__*/_jsx("span", {
          style: {
            cursor: 'help'
          },
          children: /*#__PURE__*/_jsx(UITooltip, {
            use: "longform",
            title: /*#__PURE__*/_jsx(TranslatedFeature, {
              englishTranslation: feature.descriptions[highestTierProduct],
              productLine: productLine,
              feature: feature,
              attribute: "description",
              needsProductLineIndexing: true
            }),
            children: /*#__PURE__*/_jsx(FeatureName, {
              children: /*#__PURE__*/_jsx(TranslatedFeature, {
                englishTranslation: feature.displayName,
                productLine: productLine,
                feature: feature,
                attribute: "name"
              })
            })
          })
        }) : /*#__PURE__*/_jsx(FeatureName, {
          children: /*#__PURE__*/_jsx(TranslatedFeature, {
            englishTranslation: feature.displayName,
            productLine: productLine,
            feature: feature,
            attribute: "name"
          })
        })]
      })
    }), highlightedProducts.map(apiName => {
      const localProductLine = getProductLineFromApiNameString(apiName);
      return /*#__PURE__*/_jsx(FeatureTableCell, {
        apiName: apiName,
        feature: feature,
        showTooltip: true,
        productLine: localProductLine,
        onlyShowCurrentLongLimit: onlyShowCurrentLongLimit
      }, apiName);
    })]
  }, feature.featureId);
};

export default FeatureTableRow;